import { useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

/** Full-screen image carousel for the Property Photos gallery.
 *
 *  Keeps the buyer on the report page (rather than dumping them in a new tab),
 *  which preserves the "Talk to Wholesaler" CTA + click-tracking pixel context.
 *  Keyboard nav: ←/→ to step, Esc to close. Backdrop click also closes.
 */
export default function PhotoLightbox({ photos, index, onChange, onClose, onPhotoView }) {
  const total = photos.length;
  const current = photos[index];

  const next = useCallback(() => {
    if (total <= 1) return;
    onChange((index + 1) % total);
  }, [index, total, onChange]);

  const prev = useCallback(() => {
    if (total <= 1) return;
    onChange((index - 1 + total) % total);
  }, [index, total, onChange]);

  // Fire onPhotoView whenever the visible photo changes.
  useEffect(() => {
    if (onPhotoView && Number.isInteger(index)) onPhotoView(index);
  }, [index, onPhotoView]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { onClose(); }
      else if (e.key === "ArrowRight") { next(); }
      else if (e.key === "ArrowLeft") { prev(); }
    };
    window.addEventListener("keydown", onKey);
    // Prevent body scroll while the lightbox is open.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [next, prev, onClose]);

  if (!current) return null;

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center p-4 sm:p-8 print:hidden"
      onClick={onClose}
      data-testid="photo-lightbox"
    >
      {/* Counter — top-left */}
      <div className="absolute top-4 left-4 text-white/80 text-sm font-mono tabular-nums" data-testid="lightbox-counter">
        {index + 1} / {total}
      </div>

      {/* Close — top-right */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center transition-colors"
        data-testid="lightbox-close"
        aria-label="Close"
      >
        <X size={20} />
      </button>

      {/* Prev — only if 2+ photos */}
      {total > 1 && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); prev(); }}
          className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center transition-colors"
          data-testid="lightbox-prev"
          aria-label="Previous photo"
        >
          <ChevronLeft size={28} />
        </button>
      )}

      {/* Image */}
      <img
        src={current}
        alt={`Property photo ${index + 1}`}
        className="max-w-full max-h-full object-contain select-none"
        onClick={(e) => e.stopPropagation()}
        draggable={false}
        data-testid="lightbox-image"
      />

      {/* Next — only if 2+ photos */}
      {total > 1 && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); next(); }}
          className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center transition-colors"
          data-testid="lightbox-next"
          aria-label="Next photo"
        >
          <ChevronRight size={28} />
        </button>
      )}

      {/* Hint — bottom centered */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/40 text-[10px] uppercase tracking-widest hidden sm:block">
        ← → to navigate · Esc to close
      </div>
    </div>
  );
}
